import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { FaBackward, FaEdit, FaSave } from "react-icons/fa";
import { Container } from "reactstrap";
import TextEditor from "../../../../components/TextEditor";
import { useLocation, useNavigate } from "react-router-dom";
import { GamesAPI } from "../../../../api/games";
import SPINNER from "../../../../components/Spinner";

interface SectionWrapperProps {
  children: React.ReactNode;
  header: string;
}
const SectionWrapper = (props: SectionWrapperProps) => {
  const { header, children } = props;
  return (
    <section className="w-full h-fit my-3 flex flex-col items-center">
      <h2 className="border-b pb-4 pl-3 mb-3 font-semibold text-gray-600 w-full">
        {header}
      </h2>
      <div className="w-full flex flex-col">{children}</div>
    </section>
  );
};
interface FormData {
  contentName: string;
  description: string;
  externalLink: string;
  picture: string;
  status: string;
  seoTitle: string;
  seoKeywords: string;
  seoDesc: string;
}
const initialFormData: FormData = {
  contentName: "",
  description: "",
  externalLink: "",
  picture: "",
  status: "active",
  seoTitle: "",
  seoKeywords: "",
  seoDesc: "",
};

const AddNewGame = () => {
  document.title = "Casino24 - Add New Game";
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [loading, setLoading] = useState<boolean>(false);
  const [picture, setPicture] = useState<File>();

  useEffect(() => {
    const fetchGame = async () => {
      setLoading(true);
      if (state && state.id) {
        const res = await GamesAPI.getGame(state.id);
        if (res.data) {
          setFormData(res.data);
        }
      }
      setLoading(false);
    };
    fetchGame();
  }, [state]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, files } = e.target as HTMLInputElement;

    if (name === "picture" && files && files.length > 0) {
      setPicture(files[0]);
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: files && files.length > 0 ? files[0] : value,
      }));
    }
  };

  const handleEditorChange = (name: string, value: string) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    let pictureUrl = formData.picture;
    if (picture !== undefined) {
      const res = (await GamesAPI.uploadImage(picture)) as {
        url?: string;
        success?: boolean;
      };
      if (res.success && res.url) {
        pictureUrl = res.url;
      } else alert("Something went wrong P. Try Again!");
    }

    const dataToSend = {
      contentName: formData.contentName,
      description: formData.description,
      externalLink: formData.externalLink,
      picture: pictureUrl,
      status: formData.status,
      seoTitle: formData.seoTitle,
      seoKeywords: formData.seoKeywords,
      seoDesc: formData.seoDesc,
    };

    try {
      if (state && state.id) {
        await GamesAPI.updateGame(state.id, dataToSend);
        navigate("/vips/games-list");
      } else {
        const res = await GamesAPI.saveGame(dataToSend);
        if (res.data.id) {
          setFormData(initialFormData);
          navigate("/vips/games-list");
        }
      }
    } catch (error) {
      alert("Something went wrong D! Try Again.");
    }

    setLoading(false);
  };
  return (
    <Container
      fluid
      className="p-0 m-0 flex flex-col text-black w-full min-h-screen bg-[#eeeeee] relative"
    >
      {/* Header */}
      <section className="p-4 text-[3xl] w-full h-fit bg-white font-bold">
        <h2>Add New Game</h2>
      </section>

      {/* Form Section */}
      <section className="flex-1 p-4 justify-center items-center">
        {/* Form */}
        <form
          className="bg-white h-full w-full flex flex-col p-3"
          onSubmit={handleSubmit}
        >
          {/* Informations */}
          <SectionWrapper header="Informations">
            {/* Content Name */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="contentName"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Content Name
              </label>
              <input
                type="text"
                id="contentName"
                name="contentName"
                onChange={handleInputChange}
                value={formData.contentName}
                required
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {/*  Description */}
            <div className="grid grid-cols-1 grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="description"
                className="block text-[0.8rem] text-gray-400 w-full items-start lg:justify-end md:px-3 flex"
              >
                Game Description
              </label>
              <div className="col-span-5">
                <TextEditor
                  name="description"
                  value={formData.description}
                  onChange={handleEditorChange}
                  height={400}
                  language="en"
                />
              </div>
            </div>
            {/* External Link */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="externalLink"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                External Link (If Any)
              </label>
              <input
                type="text"
                id="externalLink"
                name="externalLink"
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
                value={formData.externalLink}
              />
            </div>
            {/* Profile Picture */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="picture"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Profile Picture
              </label>
              <input
                type="file"
                id="picture"
                name="picture"
                accept="image/*"
                onChange={handleInputChange}
                required={formData.picture !== "" ? false : true}
                className="p-2 w-full border text-[0.8rem] col-span-5"
              />
            </div>
            {formData.picture !== "" && (
              <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
                <div />
                <div className="p-2 w-full col-span-5">
                  <img
                    alt={formData.contentName ?? "Preview"}
                    src={formData.picture}
                    className="w-64 h-64 object-cover"
                  />
                </div>
              </div>
            )}
            {/* Status */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="status"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                Status
              </label>
              <select
                id="status"
                name="status"
                onChange={handleInputChange}
                required
                className="p-2 w-full text-[0.8rem] col-span-5"
                value={formData.status}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </SectionWrapper>

          {/* SEO */}
          <SectionWrapper header="SEO">
            {/* SEO Title */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="seoTitle"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                SEO Title
              </label>
              <input
                type="text"
                id="seoTitle"
                name="seoTitle"
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
                value={formData.seoTitle}
              />
            </div>
            {/* SEO Keywords */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="seoKeywords"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                SEO Keyword
              </label>
              <input
                type="text"
                id="seoKeywords"
                name="seoKeywords"
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
                value={formData.seoKeywords}
              />
            </div>
            {/* SEO Descriptiom */}
            <div className="grid grid-cols-1 md:grid-cols-6 w-full gap-2 md:gap-3 mb-3">
              <label
                htmlFor="seoDesc"
                className="block text-[0.8rem] text-gray-400 w-full items-center lg:justify-end md:px-3 flex"
              >
                SEO Description
              </label>
              <textarea
                id="seoDesc"
                name="seoDesc"
                onChange={handleInputChange}
                className="p-2 w-full border text-[0.8rem] col-span-5"
                value={formData.seoDesc}
              />
            </div>
          </SectionWrapper>

          <div className="flex justify-end items-center">
            <button
              type="submit"
              className="bg-green-600 text-white p-2 px-4 rounded-md mx-2 flex items-center"
            >
              {state && state.id ? (
                <>
                  <FaEdit className="mr-2" /> Update
                </>
              ) : (
                <>
                  <FaSave className="mr-2" /> Save
                </>
              )}
            </button>
            <button
              type="button"
              className="bg-gray-600 text-white p-2 px-4 rounded-md flex items-center"
              onClick={() => navigate("/vips/games-list")}
            >
              <FaBackward className="mr-2" /> Back
            </button>
          </div>
        </form>
      </section>
      {loading && (
        <div className="absolute inset-0 bg-gray-300/30 flex items-center justify-center backdrop-blur-sm">
          <SPINNER />
        </div>
      )}
    </Container>
  );
};

export default AddNewGame;
